// /tapes — My Tapes: your own tapes (playlists of up to 100 songs) and your bookmarked Top 100
// tapes. /tapes/:id — one tape: the cassette and equaliser on the black screen, play/skip, and
// the songs (reorder, remove), rename and delete.
import { useEffect, useState } from "react";
import { clockFor, type Bpm } from "@eb/shared";
import { api, ApiError } from "../api";
import { engineHost } from "../audio/host";
import { currentRoute, Link, navigate, useTitle } from "../lib/router";
import { Visualizer } from "../river/Visualizer";
import { useSession } from "../session";
import { Cassette } from "../tapes/Cassette";
import { playTape, stopTape, useTapePlayer, type TapeTrack } from "../tapes/tapePlayer";
import { streamHref, STREAM_BPM } from "../stream";

interface TapeSummary {
  id: string;
  name: string;
  songs: number;
  updatedAt: number;
}
interface TapeView {
  id: string;
  name: string;
  songs: (TapeTrack & { kind: "human" | "filler" })[];
}

function SignInFirst() {
  return (
    <p>
      <Link href={`/signin?next=${encodeURIComponent(currentRoute())}`}>Sign in</Link> to make your own tapes and
      bookmark Top 100 tapes.
    </p>
  );
}

export function MyTapes() {
  useTitle("My Tapes");
  const user = useSession((s) => s.user);
  const loaded = useSession((s) => s.loaded);
  const [data, setData] = useState<{
    tapes: TapeSummary[];
    bookmarks: { bpm: Bpm; day: number; date: string }[];
  } | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!user?.nickname) return;
    api<{ tapes: TapeSummary[]; bookmarks: { bpm: Bpm; day: number; date: string }[] }>("GET", "/api/tapes")
      .then(setData)
      .catch((e) => setError(String(e)));
  }, [user?.nickname]);

  return (
    <main id="main" className="page my-tapes-page">
      <h1>My Tapes</h1>
      <p className="muted">
        Your own tapes of up to 100 songs. Add songs with “+ Tape” on a song's card or page — or live while one plays in
        the River.
      </p>
      {!loaded ? null : !user?.nickname ? (
        <SignInFirst />
      ) : (
        <>
          <form
            className="row tape-create"
            onSubmit={(e) => {
              e.preventDefault();
              const n = name.trim();
              if (!n) return;
              api<{ tape: { id: string } }>("POST", "/api/tapes", { name: n })
                .then((r) => navigate(`/tapes/${encodeURIComponent(r.tape.id)}`))
                .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
            }}
          >
            <label className="field">
              <span>New tape</span>
              <input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="Name" />
            </label>
            <button type="submit" className="btn btn-primary" disabled={!name.trim()} data-testid="tape-create">
              Create tape
            </button>
          </form>
          {error && <p role="alert">{error}</p>}
          <section aria-label="Your tapes">
            <h2>Your tapes</h2>
            {data && data.tapes.length === 0 && <p className="muted">No tapes yet.</p>}
            <ul className="tape-list" data-testid="tape-list">
              {data?.tapes.map((t) => (
                <li key={t.id}>
                  <Link href={`/tapes/${encodeURIComponent(t.id)}`} className="tape-row panel">
                    <span className="tape-icon" aria-hidden="true">
                      ▭
                    </span>
                    <strong>{t.name}</strong>
                    <span className="muted small">
                      {t.songs} {t.songs === 1 ? "song" : "songs"}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
          <section aria-label="Bookmarked Top 100 tapes">
            <h2>Bookmarked Top 100 tapes</h2>
            {data && data.bookmarks.length === 0 && (
              <p className="muted">
                None yet — bookmark a day's tape in <Link href="/mixtapes">Top100 Mixtapes</Link>.
              </p>
            )}
            <ul className="tape-list" data-testid="bookmark-list">
              {data?.bookmarks.map((b) => {
                const inner = (
                  <>
                    <span className="tape-icon" aria-hidden="true">
                      ★
                    </span>
                    <strong>
                      Top 100{b.bpm === 72 ? " · 72 BPM" : ""} ·{" "}
                      {new Date(`${b.date}T12:00:00Z`).toLocaleDateString(undefined, {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    </strong>
                  </>
                );
                return (
                  <li key={`${b.bpm}:${b.day}`}>
                    {/* A bookmark of the other site's tape opens that site. */}
                    {(b.bpm ?? 125) === STREAM_BPM ? (
                      <Link href={`/mixtapes/${b.day}`} className="tape-row panel">
                        {inner}
                      </Link>
                    ) : (
                      <a href={streamHref(b.bpm, `/mixtapes/${b.day}`)} className="tape-row panel">
                        {inner}
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        </>
      )}
    </main>
  );
}

export function TapePage({ id }: { id: string }) {
  useTitle("Tape");
  const user = useSession((s) => s.user);
  const loaded = useSession((s) => s.loaded);
  const [tape, setTape] = useState<TapeView | null>(null);
  const [error, setError] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const m = useTapePlayer();
  useEffect(() => {
    if (!user?.nickname) return;
    api<{ tape: TapeView }>("GET", `/api/tapes/${encodeURIComponent(id)}`)
      .then((r) => setTape(r.tape))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [id, user?.nickname]);

  if (!loaded) return null;
  if (!user?.nickname)
    return (
      <main id="main" className="page">
        <SignInFirst />
      </main>
    );
  if (error)
    return (
      <main id="main" className="page">
        <p role="alert">{error}</p>
        <Link href="/tapes">← My Tapes</Link>
      </main>
    );
  if (!tape)
    return (
      <main id="main" className="page">
        <p className="muted">Loading…</p>
      </main>
    );

  const mine = m.tapeId === tape.id && (m.playing || m.starting);
  const current = mine ? tape.songs[m.index] : null;
  const start = (index: number) => {
    const host = engineHost(); // audio unlock inside the click
    void playTape(host, tape.id, tape.songs, index);
  };
  const update = (p: Promise<{ tape: TapeView }>) =>
    p.then((r) => setTape(r.tape)).catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  const move = (i: number, d: number) => {
    const ids = tape.songs.map((s) => s.loopId);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    void update(api("PUT", `/api/tapes/${encodeURIComponent(tape.id)}/order`, { loopIds: ids }));
  };
  const total = tape.songs.length;
  const progress = total ? (m.index + m.progress) / total : 0;

  return (
    <main id="main" className="page countdown-page tape-page">
      <p>
        <Link href="/tapes">← My Tapes</Link>
      </p>
      {renaming ? (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void update(api("PUT", `/api/tapes/${encodeURIComponent(tape.id)}`, { name })).then(() =>
              setRenaming(false),
            );
          }}
        >
          <input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} aria-label="Tape name" />
          <button type="submit" className="btn btn-primary">
            Save
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setRenaming(false)}>
            Cancel
          </button>
        </form>
      ) : (
        <h1 data-testid="tape-name">{tape.name}</h1>
      )}
      <section className="countdown-display oled" aria-live="polite" data-testid="tape-display">
        <Cassette playing={mine && m.playing} progress={mine ? progress : 0} label={tape.name} />
        {current ? (
          <>
            <span className="countdown-song">
              <strong>
                {m.index + 1}/{total} ·{" "}
                {current.title ?? (current.author === "Wizard" ? "Wizard song" : "Untitled song")}
              </strong>{" "}
              by {current.author ?? "a former contributor"}
            </span>
            <Visualizer playing={m.playing} />
            <span className="countdown-progress" aria-hidden="true">
              <span style={{ width: `${Math.round(m.progress * 100)}%` }} />
            </span>
          </>
        ) : (
          <span className="countdown-song">
            {total} {total === 1 ? "song" : "songs"} · about{" "}
            {Math.round(tape.songs.reduce((ms, s) => ms + clockFor(s.bpm ?? 125).SLOT_MS, 0) / 60_000)} min
          </span>
        )}
      </section>
      <div className="row countdown-controls">
        {total === 0 ? (
          <span className="muted">Add songs with “+ Tape” on any song.</span>
        ) : mine ? (
          <>
            <button type="button" className="btn" onClick={() => start(Math.max(0, m.index - 1))}>
              ⏮ Back
            </button>
            <button type="button" className="btn btn-primary" onClick={stopTape} data-testid="tape-stop">
              ■ Stop
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => (m.index + 1 < total ? start(m.index + 1) : stopTape())}
              data-testid="tape-next"
            >
              Next ⏭
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-primary" onClick={() => start(0)} data-testid="tape-play">
            ▶ Play the tape
          </button>
        )}
        {m.error && (
          <span role="alert" className="error">
            {m.error}
          </span>
        )}
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-small btn-ghost"
          onClick={() => {
            setName(tape.name);
            setRenaming(true);
          }}
        >
          Rename
        </button>
        <button
          type="button"
          className="btn btn-small btn-ghost"
          onClick={() => {
            if (!window.confirm(`Delete the tape “${tape.name}”?`)) return;
            stopTape();
            void api("DELETE", `/api/tapes/${encodeURIComponent(tape.id)}`).then(() => navigate("/tapes"));
          }}
          data-testid="tape-delete"
        >
          Delete tape
        </button>
      </div>
      <ol className="countdown-list" data-testid="tape-songs">
        {tape.songs.map((s, i) => (
          <li key={s.loopId} className={current?.loopId === s.loopId ? "now" : ""}>
            <button
              type="button"
              className="countdown-row"
              onClick={() => start(i)}
              aria-label={`Play from song ${i + 1}`}
            >
              <span className="countdown-num">{i + 1}</span>
              <span className="countdown-title">
                {s.title ?? (s.author === "Wizard" ? "Wizard song" : "Untitled song")}
              </span>
              <span className="muted">
                {s.author ?? "former contributor"}
                {s.bpm === 72 && " · 72 BPM"}
              </span>
              <span />
            </button>
            <button
              type="button"
              className="btn btn-small btn-ghost"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              aria-label="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              className="btn btn-small btn-ghost"
              onClick={() => move(i, 1)}
              disabled={i === total - 1}
              aria-label="Move down"
            >
              ↓
            </button>
            <button
              type="button"
              className="btn btn-small btn-ghost"
              onClick={() =>
                void update(
                  api("DELETE", `/api/tapes/${encodeURIComponent(tape.id)}/songs/${encodeURIComponent(s.loopId)}`),
                )
              }
              aria-label={`Remove song ${i + 1}`}
            >
              ✕
            </button>
          </li>
        ))}
      </ol>
    </main>
  );
}
